import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Rating,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Assessment as EvaluateIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Grade as ScoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import api from '../../../api';
import { showSuccess, showError } from '../../../utils/sweetAlertConfig';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

const CandidateEvaluation = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [evaluationData, setEvaluationData] = useState({
    score: 5,
    notes: ''
  });

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/candidates-for-evaluation');
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error loading candidates:', error);
      showError('Error', 'Failed to load candidates data');
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = (candidate) => {
    setSelectedCandidate(candidate);
    setEvaluationData({
      score: candidate.final_interview_score || 5,
      notes: candidate.evaluation_notes || ''
    });
    setEvaluationDialogOpen(true);
  };

  const handleSaveEvaluation = async () => {
    try {
      await api.post(`/candidates/${selectedCandidate.candidate_id}/evaluate`, {
        score: evaluationData.score,
        notes: evaluationData.notes
      });

      showSuccess('Success!', 'Evaluation saved successfully');
      setEvaluationDialogOpen(false);
      loadCandidates();
    } catch (error) {
      console.error('Error saving evaluation:', error);
      showError('Error', 'Failed to save evaluation');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return SUCCESS;
    if (score >= 6) return '#ed6c02';
    if (score >= 4) return '#757575';
    return '#d32f2f';
  };

  const getScoreLabel = (score) => {
    if (score >= 9) return 'Excellent';
    if (score >= 7) return 'Very Good';
    if (score >= 5) return 'Good';
    if (score >= 3) return 'Fair';
    return 'Poor';
  };

  const columns = [
    {
      field: 'full_name',
      label: 'Candidate',
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: PRIMARY, width: 36, height: 36 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>
              {row.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.candidate_id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      label: 'Email',
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      label: 'Phone',
      width: 150,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'scores',
      label: 'Scores',
      width: 180,
      render: (row) => (
        <Box>
          {row.final_exam_score !== null && (
            <Chip
              size="small"
              label={`Exam: ${row.final_exam_score}%`}
              sx={{
                bgcolor: '#1976d215',
                color: '#1976d2',
                fontWeight: 600,
                mb: 0.5,
              }}
            />
          )}
          {row.final_interview_score !== null && (
            <Chip
              size="small"
              label={`Interview: ${row.final_interview_score}/10`}
              sx={{
                bgcolor: `${getScoreColor(row.final_interview_score)}15`,
                color: getScoreColor(row.final_interview_score),
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'rating',
      label: 'Current Rating',
      width: 150,
      align: 'center',
      render: (row) => (
        <Box sx={{ textAlign: 'center' }}>
          {row.final_interview_score !== null ? (
            <Box>
              <Typography variant="h6" sx={{ color: PRIMARY, fontWeight: 700 }}>
                {row.final_interview_score}/10
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getScoreLabel(row.final_interview_score)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not Rated
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const filteredCandidates = candidates.filter(
    (candidate) =>
      candidate.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.candidate_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCandidates = filteredCandidates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Candidate Evaluation"
        subtitle="Evaluate and rate candidates based on their performance"
        icon={ScoreIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Candidate Evaluation' },
        ]}
      />

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by candidate name, ID, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
            },
          }}
        />
      </Box>

      {/* Candidates Table */}
      <ModernTable
        columns={columns}
        data={paginatedCandidates}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={filteredCandidates.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onEdit={handleEvaluate}
        emptyMessage="No candidates available for evaluation"
        showActions={true}
        onView={null}
        onDelete={null}
      />

      {/* Evaluation Dialog */}
      <Dialog
        open={evaluationDialogOpen}
        onClose={() => setEvaluationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: PRIMARY,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
          }}
        >
          <EvaluateIcon />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
            Evaluate Candidate: {selectedCandidate?.full_name}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedCandidate && (
            <Grid container spacing={3}>
              {/* Candidate Info Card */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f8f9fa', borderRadius: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: PRIMARY, width: 50, height: 50 }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
                              {selectedCandidate.full_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedCandidate.candidate_id}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          {selectedCandidate.final_exam_score !== null && (
                            <Chip
                              label={`Exam Score: ${selectedCandidate.final_exam_score}%`}
                              sx={{
                                bgcolor: '#1976d215',
                                color: '#1976d2',
                                fontWeight: 600,
                                mb: 1,
                                mr: { xs: 0, md: 1 },
                              }}
                            />
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {selectedCandidate.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedCandidate.phone}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Rating Section */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: PRIMARY, fontWeight: 600 }}>
                    Overall Candidate Rating
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Rating:
                    </Typography>
                    <Rating
                      value={evaluationData.score}
                      onChange={(event, newValue) => {
                        setEvaluationData(prev => ({ ...prev, score: newValue || 1 }));
                      }}
                      max={10}
                      size="large"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: SUCCESS
                        },
                        '& .MuiRating-iconEmpty': {
                          color: '#e0e0e0'
                        }
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
                      {evaluationData.score}/10
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: SUCCESS, fontWeight: 600 }}>
                    {getScoreLabel(evaluationData.score)}
                  </Typography>
                </Box>
              </Grid>

              {/* Notes Section */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Evaluation Notes (Optional)"
                  value={evaluationData.notes}
                  onChange={(e) => setEvaluationData(prev => ({ ...prev, notes: e.target.value }))}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: PRIMARY
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: PRIMARY
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: PRIMARY
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setEvaluationDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#333',
                color: '#333'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEvaluation}
            variant="contained"
            startIcon={<ScoreIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              bgcolor: SUCCESS,
              '&:hover': {
                bgcolor: '#5a9e67'
              }
            }}
          >
            Save Evaluation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CandidateEvaluation;
